import { rotatedBounds, type Point } from '../doc/geometry';
import type { Item, NewItem, NoteItem } from '../doc/schema';
import type { Ctx } from './actions';
import { writeClipboardText } from './clipboard';
import { flash } from './flash';
import { boardNoteLook, notePropsFor } from './noteLook';

/**
 * Lists and sticky notes. A pasted bullet or numbered list becomes one note per item, in a grid
 * that reads like the list (left to right, then down). "Copy as list" goes the other way: the
 * selected notes become a bullet list in the same reading order, so a list survives the round trip.
 */

/** Longest text a pasted note keeps (the same cap as a single pasted note). */
export const PASTED_NOTE_MAX = 5000;
/** Longer than this, a "list" is not meant as notes: it pastes as one note. */
const MAX_LIST_NOTES = 1000;

// ---- plain text -------------------------------------------------------------

/**
 * A list marker and the white space after it: markdown bullets, typographic bullets, arrows and
 * boxes, numbers ("1." "1)" "(1)") and letters ("a)"). Word writes "•" (or the Symbol font's
 * U+F0B7) and a tab, and "o" or "§" and a tab on deeper levels. The white space is required, so
 * "-5" and "*word*" stay text.
 */
const MARKER = /^(?:[-*+•◦▪▫‣⁃●○■□►▸➤→–·☐☑☒✓✔\uf0b7]|\d{1,3}[.)]|\(\d{1,3}\)|[a-zA-Z]\)|[o§](?=\t))(?:[ \t\u00a0]+|$)/;
/** A markdown task box after the marker ("- [ ] task"). */
const TASK_BOX = /^\[[ xX]\][ \t]+/;
const LEADING_SPACE = /^[ \t\u00a0\u2000-\u200b\u3000]*/;
/** Markdown bold, as chat assistants write it. */
const BOLD = /\*\*(\S(?:.*?\S)?)\*\*/g;

/** Width of leading white space, tabs to the next multiple of 4. */
function indentWidth(space: string): number {
  let w = 0;
  for (const ch of space) w = ch === '\t' ? w + 4 - (w % 4) : w + 1;
  return w;
}

interface Draft {
  first: string;
  /** Lines after the item's first, with their indent; a blank line keeps a paragraph break. */
  more: { indent: number; text: string }[];
}

function draftText(d: Draft): string {
  let cut = Infinity;
  for (const l of d.more) if (l.text && l.indent < cut) cut = l.indent;
  const lines = [d.first, ...d.more.map((l) => (l.text ? ' '.repeat(l.indent - cut) + l.text : ''))];
  return lines.join('\n').replace(BOLD, '$1').trim().slice(0, PASTED_NOTE_MAX);
}

/**
 * The items of a plain-text list, or null unless the text is one: two or more items, and every
 * line an item or part of one. Lines indented under an item (nested items, further paragraphs)
 * stay with it, dedented, and so do unindented lines right below it (a wrapped line). Text
 * before the first item, or an unindented paragraph after a blank line, makes it ordinary text.
 */
export function parseListText(text: string): string[] | null {
  const drafts: Draft[] = [];
  let base = -1;
  let gap = false;
  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    if (!raw.trim()) {
      gap = drafts.length > 0;
      continue;
    }
    const space = LEADING_SPACE.exec(raw)?.[0] ?? '';
    const indent = indentWidth(space);
    const body = raw.slice(space.length).trimEnd();
    const marker = MARKER.exec(body);
    // an item at the list's own level (a stray space either way is forgiven); deeper ones are nested
    if (marker && (base < 0 || indent <= base + 1)) {
      if (base < 0) base = indent;
      drafts.push({ first: body.slice(marker[0].length).replace(TASK_BOX, ''), more: [] });
      gap = false;
      continue;
    }
    const draft = drafts[drafts.length - 1];
    if (!draft || (gap && indent <= base)) return null;
    if (gap) draft.more.push({ indent: 0, text: '' });
    draft.more.push({ indent, text: body });
    gap = false;
  }
  const items = drafts.map(draftText).filter(Boolean);
  return items.length >= 2 && items.length <= MAX_LIST_NOTES ? items : null;
}

// ---- HTML -------------------------------------------------------------------

const BLOCKS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE', 'TABLE', 'TR', 'DT', 'DD', 'FIGCAPTION', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER']);
const SKIPPED = new Set(['STYLE', 'SCRIPT', 'TEMPLATE']);
const isList = (el: Element) => el.tagName === 'UL' || el.tagName === 'OL';

/** Text of list content: blocks and line breaks start lines, nested items become "- " lines indented by depth. */
function listContentText(content: Node[]): string {
  const lines = [{ pre: '', text: '' }];
  const last = () => lines[lines.length - 1]!;
  // an empty line is reused rather than stacked; `force` gives it the new prefix
  const breakLine = (pre: string, force = false) => {
    if (last().text.trim()) lines.push({ pre, text: '' });
    else if (force) last().pre = pre;
  };
  const list = (el: Element, depth: number): void => {
    for (const child of Array.from(el.children)) {
      // Google Docs puts a sub-list beside the items instead of inside one
      if (isList(child)) list(child, depth + 1);
      else {
        breakLine(`${'  '.repeat(depth)}- `, true);
        walk(Array.from(child.childNodes), depth + 1);
      }
    }
    breakLine('  '.repeat(depth), true);
  };
  const walk = (nodes: Node[], depth: number): void => {
    for (const node of nodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        last().text += node.textContent ?? '';
        continue;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      const el = node as Element;
      if (SKIPPED.has(el.tagName)) continue;
      if (isList(el)) list(el, depth);
      else if (el.tagName === 'BR') breakLine('  '.repeat(depth));
      else if (BLOCKS.has(el.tagName)) {
        breakLine('  '.repeat(depth));
        walk(Array.from(el.childNodes), depth);
        breakLine('  '.repeat(depth));
      } else walk(Array.from(el.childNodes), depth);
    }
  };
  walk(content, 0);
  return lines
    .map((l) => ({ pre: l.pre, text: l.text.replace(/\s+/g, ' ').trim() }))
    .filter((l) => l.text)
    .map((l) => l.pre + l.text)
    .join('\n')
    .slice(0, PASTED_NOTE_MAX);
}

/**
 * The items of HTML lists (lists copied from web pages and rich editors, whose plain text has no
 * bullets), or null unless the HTML is nothing but lists with two or more items between them.
 */
export function parseListHtml(html: string): string[] | null {
  if (!/<li[\s>]/i.test(html)) return null;
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  const lists = Array.from(body.querySelectorAll('ul, ol')).filter((l) => !l.parentElement?.closest('ul, ol'));
  // a heading or a paragraph around the lists makes it mixed text
  const rest = body.cloneNode(true) as HTMLElement;
  rest.querySelectorAll('ul, ol, style, script, template').forEach((el) => el.remove());
  if (!lists.length || rest.textContent?.trim()) return null;
  const items: string[] = [];
  for (const list of lists) {
    for (const child of Array.from(list.children)) {
      if (!isList(child)) {
        items.push(listContentText(Array.from(child.childNodes)));
        continue;
      }
      // a sub-list beside the items (Google Docs) belongs to the item above it
      const sub = listContentText([child]);
      const prev = items.length ? items[items.length - 1] : undefined;
      if (prev !== undefined) items[items.length - 1] = prev ? `${prev}\n${sub}` : sub;
      else items.push(sub);
    }
  }
  const out = items.filter(Boolean);
  return out.length >= 2 && out.length <= MAX_LIST_NOTES ? out : null;
}

/**
 * The items of a pasted list, or null: the plain text when it is a list, else an HTML list (web
 * pages and rich editors leave the bullets out of the plain text).
 */
export function pastedList(data: DataTransfer | null): string[] | null {
  if (!data) return null;
  return parseListText(data.getData('text/plain')) ?? parseListHtml(data.getData('text/html'));
}

// ---- notes from a list --------------------------------------------------------

/** Columns for a grid of n notes: close to a 16:9 block, preferring a well-filled last row. */
export function gridColumns(n: number, w: number, h: number, gap: number): number {
  let best = 1;
  let bestCost = Infinity;
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols);
    const aspect = (cols * (w + gap) - gap) / (rows * (h + gap) - gap);
    const cost = Math.abs(Math.log(aspect / 1.8)) + (rows * cols - n) / cols;
    if (cost < bestCost - 1e-9) {
      best = cols;
      bestCost = cost;
    }
  }
  return best;
}

/** One note per item in the board's default look, in a grid centred on `at` that reads like the list. One undo step. */
export function createNotesFromList(ctx: Ctx, texts: string[], at: Point): string[] {
  const look = boardNoteLook(ctx.board);
  const gap = Math.max(16, Math.round(Math.min(look.w, look.h) * 0.12));
  const cols = gridColumns(texts.length, look.w, look.h, gap);
  const rows = Math.ceil(texts.length / cols);
  const x0 = Math.round(at.x - (cols * (look.w + gap) - gap) / 2);
  const y0 = Math.round(at.y - (rows * (look.h + gap) - gap) / 2);
  const ids = ctx.board.createItems(
    texts.map(
      (text, i): NewItem => ({
        type: 'note',
        x: x0 + (i % cols) * (look.w + gap),
        y: y0 + Math.floor(i / cols) * (look.h + gap),
        w: look.w,
        h: look.h,
        props: notePropsFor(look, text),
      }),
    ),
  );
  ctx.pane.getState().select(ids);
  return ids;
}

// ---- a list from notes --------------------------------------------------------

/**
 * Items in reading order: rows from the top, each left to right. An item joins a row while its
 * centre is above the bottom of the row's first item, so roughly level notes count as one row.
 */
export function readingOrder<T extends Item>(items: T[]): T[] {
  const boxes = items.map((item) => ({ item, b: rotatedBounds(item) }));
  boxes.sort((a, b) => a.b.y + a.b.h / 2 - (b.b.y + b.b.h / 2));
  const rows: (typeof boxes)[] = [];
  let bottom = -Infinity;
  for (const box of boxes) {
    const row = rows[rows.length - 1];
    if (row && box.b.y + box.b.h / 2 < bottom) row.push(box);
    else {
      rows.push([box]);
      bottom = box.b.y + box.b.h;
    }
  }
  return rows.flatMap((row) => row.sort((a, b) => a.b.x + a.b.w / 2 - (b.b.x + b.b.w / 2)).map((r) => r.item));
}

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * The notes as a bullet list in reading order, one bullet per note: markdown "- " bullets with a
 * note's further lines indented under its bullet, and the same list as HTML. Empty notes are left out.
 */
export function notesAsList(notes: NoteItem[]): { text: string; html: string; count: number } {
  const texts = readingOrder(notes)
    .map((n) => n.props.text.replace(/\r\n?/g, '\n').trim())
    .filter(Boolean);
  const text = texts
    .map((t) =>
      t
        .split('\n')
        .map((line, i) => (i === 0 ? `- ${line.trimEnd()}` : line.trim() ? `  ${line.trimEnd()}` : ''))
        .join('\n'),
    )
    .join('\n');
  const html = `<ul>${texts.map((t) => `<li>${t.split('\n').map(escapeHtml).join('<br>')}</li>`).join('')}</ul>`;
  return { text, html, count: texts.length };
}

/** "Copy as list": the notes on the system clipboard as a bullet list, confirmed by a caption on the pane. */
export async function copyNotesAsList(notes: NoteItem[], from: Element): Promise<void> {
  const pane = from.closest('.pane') ?? from;
  const list = notesAsList(notes);
  if (!list.count) return flash(pane, 'These notes are empty');
  const ok = await writeClipboardText(list.text, list.html);
  flash(pane, ok ? `Copied ${list.count} ${list.count === 1 ? 'note' : 'notes'} as a list` : 'The clipboard is not available here');
}
